import React, { useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Link,
  Tooltip,
  Typography,
} from '@mui/material';
import { PlayArrow as StartIcon } from '@mui/icons-material';
import { Run } from '../services/types';

// NC-778 (D6): mirrors the BE dispatch lock (shared/provisionService.ts
// LOCK_WINDOW_MS) so the button disables instead of running into a 409.
export const PROVISION_LOCK_WINDOW_MS = 30 * 60 * 1000;

/**
 * NC-778 (D6) / NC-1576: FE mirror of `isWithinLockWindow` in the API.
 *
 * Locked while the last dispatch is younger than the window — unless
 * mark-run-error.ps1 recorded a failure (`erroredAtUtc`) at/after that dispatch:
 * then the job is over and a retry must be possible right away. Incident
 * 4C9EF023: the job died after 7 minutes and the retry stayed locked for 30.
 * Compared at second resolution because `erroredAtUtc` carries no millis.
 */
export function isProvisionLockActive(
  run: Pick<Run, 'provisioningStartedUtc' | 'erroredAtUtc'>,
  now: number = Date.now()
): boolean {
  const startedMs = run.provisioningStartedUtc ? Date.parse(run.provisioningStartedUtc) : NaN;
  if (!Number.isFinite(startedMs) || now - startedMs >= PROVISION_LOCK_WINDOW_MS) return false;
  const erroredMs = run.erroredAtUtc ? Date.parse(run.erroredAtUtc) : NaN;
  const failedSinceDispatch =
    Number.isFinite(erroredMs) && Math.floor(erroredMs / 1000) >= Math.floor(startedMs / 1000);
  return !failedSinceDispatch;
}

interface ProvisioningFailureAlertProps {
  run: Pick<Run, 'provisioningError' | 'provisioningJobUrl'>;
  canOperate: boolean;
  provisionLoading: boolean;
  provisionLockActive: boolean;
  onRetry: () => void;
  onOpenDiagnostics: () => void;
}

/**
 * NC-1236 (V3) / NC-1576: provisioning-failure banner on the run detail page.
 *
 * The tutor sees a short, actionable summary and the retry; the raw error text
 * (stack trace, step output) and the Actions-log link sit behind "Technische
 * Details" — in incident 4C9EF023 the raw text in the default view was what
 * made the tutor give up, not the failure itself.
 */
export const ProvisioningFailureAlert: React.FC<ProvisioningFailureAlertProps> = ({
  run,
  canOperate,
  provisionLoading,
  provisionLockActive,
  onRetry,
  onOpenDiagnostics,
}) => {
  const [showDetails, setShowDetails] = useState(false);

  return (
    <Alert severity="error" sx={{ mb: 2 }}>
      <AlertTitle>Provisionierung fehlgeschlagen</AlertTitle>
      <Typography variant="body2">
        {canOperate
          ? 'Die Bereitstellung der Schulungsumgebung ist gescheitert. Bitte „Erneut provisionieren“ klicken — bereits angelegte Teile werden wiederverwendet. Scheitert es erneut, bitte den Support kontaktieren.'
          : 'Die Bereitstellung der Schulungsumgebung ist gescheitert. Bitte einen Operator oder den Support informieren.'}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5, alignItems: 'center' }}>
        {canOperate && (
          <Tooltip
            title={
              provisionLockActive
                ? 'Provisionierung läuft bereits — erneuter Start erst nach Ablauf der 30-Minuten-Sperre'
                : 'Provisionierung für diese Schulung erneut starten'
            }
          >
            <span>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={provisionLoading ? <CircularProgress size={16} /> : <StartIcon />}
                onClick={onRetry}
                disabled={provisionLoading || provisionLockActive}
              >
                Erneut provisionieren
              </Button>
            </span>
          </Tooltip>
        )}
        <Button size="small" variant="text" color="inherit" onClick={onOpenDiagnostics}>
          Zur Diagnose
        </Button>
        <Button
          size="small"
          variant="text"
          color="inherit"
          aria-expanded={showDetails}
          onClick={() => setShowDetails((v) => !v)}
        >
          {showDetails ? 'Technische Details ausblenden' : 'Technische Details anzeigen'}
        </Button>
      </Box>
      <Collapse in={showDetails} unmountOnExit>
        <Box
          component="pre"
          sx={{
            mt: 1.5,
            mb: 0,
            p: 1,
            maxHeight: 240,
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontSize: '0.75rem',
            bgcolor: 'action.hover',
            borderRadius: 1,
          }}
        >
          {run.provisioningError ||
            'Es ist kein Detail-Fehlertext hinterlegt — bitte den Diagnose-Tab oder das GitHub-Actions-Log prüfen.'}
        </Box>
        {run.provisioningJobUrl && (
          <Link href={run.provisioningJobUrl} target="_blank" rel="noopener">
            GitHub-Actions-Log ansehen
          </Link>
        )}
      </Collapse>
    </Alert>
  );
};
